import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaHome,
  FaUsers,
  FaHistory,
  FaHeadset,
  FaPhone,
  FaBars,
  FaClock,
  FaTimes,
  FaCheckCircle,
  FaStar,
  FaChevronDown,
  FaChevronRight,
  FaFileAlt,
  FaClipboardList,
} from "react-icons/fa";
import { toast } from "react-toastify";

import { useAuth } from "../authContext/AuthProvider";
import { useAppContext } from "../context/AppContext";
import "../styles/Sidebar.css";

const LiveCallIndicator = ({ activeCall, onClick }) => {
  if (!activeCall) return null;

  return (
    <div className="erp-live-call" onClick={onClick}>
      <FaPhone className="erp-live-icon" />
      <div className="erp-live-text">
        <div className="erp-live-title">Live Call</div>
        <div className="erp-live-name">{activeCall.lead?.name}</div>
      </div>
    </div>
  );
};

const Sidebar = () => {
  const [open, setOpen] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState({});
  const { user, logout } = useAuth();
  const { activeCall } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();

  const menu = [
    { label: "Dashboard", path: "/", icon: <FaHome /> },
    { 
      label: "Proposal", 
      icon: <FaUsers />, 
      children: [
        { label: "Proposal Leads", path: "/proposals", icon: <FaFileAlt /> },
        { label: "Proposal Status", path: "/proposal-status", icon: <FaCheckCircle /> },
        { label: "Call History", path: "/history", icon: <FaHistory /> },
        { label: "Review Call Status", path: "/review-status", icon: <FaStar /> },
      ]
    },
    { label: "Sales Calls", path: "/sales-calls", icon: <FaPhone /> },
    { label: "Seasonal Calls", path: "/seasonal-calls", icon: <FaPhone /> },
    { label: "B2B Sales", path: "/b2b-calls", icon: <FaPhone /> },
    // { label: "Batch Calling", path: "/batch-calling", icon: <FaPhoneVolume /> },
    { label: "Scheduled Calls", path: "/scheduled-calls", icon: <FaClock /> },
    { 
      label: "Inbound", 
      icon: <FaHeadset />, 
      children: [
        { label: "Inbound Calls", path: "/inbound-calls", icon: <FaHeadset /> },
        { label: "Inbound SMS", path: "/inbound-sms", icon: <FaHeadset /> },
      ]
    },
    { label: "Website Chatbot", path: "/website-chatbot-leads", icon: <FaUsers /> },
    { label: "Site Reports", path: "/site-reports", icon: <FaClipboardList /> },
  ];

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const isItemActive = (item) => {
    if (item.path) return isActive(item.path);
    if (item.children) return item.children.some(child => isActive(child.path));
    return false;
  };

  const handleLogout = () => {
    logout();
    toast.success("Logged out");
    navigate("/login");
  };

  return (
    <>
      {/* Mobile Toggle */}
      <button className="erp-menu-toggle" onClick={() => setOpen(!open)}>
        {open ? <FaTimes /> : <FaBars />}
      </button>

      {/* BACKDROP (Mobile Only) */}
      {open && <div className="erp-backdrop" onClick={() => setOpen(false)} />}

      <aside className={`erp-sidebar ${open ? "open" : ""}`}>
        {/* BRAND */}
        <div className="erp-header">
          <FaHeadset className="erp-logo-icon" />
          <span className="erp-logo-text">VocaAI</span>
        </div>

        {/* NAVIGATION */}
        <nav className="erp-nav">
          {menu.map((item) => {
            if (item.children) {
              const isOpen = openSubmenus[item.label] || isItemActive(item);
              return (
                <div key={item.label}>
                  <button
                    className={`erp-nav-item ${isItemActive(item) ? "active" : ""}`}
                    onClick={() => setOpenSubmenus(prev => ({ ...prev, [item.label]: !prev[item.label] }))}
                    type="button"
                  >
                    <span className="erp-nav-icon">{item.icon}</span>
                    <span>{item.label}</span>
                    <span className="erp-nav-arrow">
                      {isOpen ? <FaChevronDown /> : <FaChevronRight />}
                    </span>
                  </button>
                  <div className={`erp-submenu ${isOpen ? 'open' : ''}`}>
                    {item.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        className={`erp-nav-item erp-nav-subitem ${isActive(child.path) ? "active" : ""}`}
                        onClick={() => setOpen(false)}
                      >
                        <span className="erp-nav-icon">{child.icon}</span>
                        <span>{child.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            } else {
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`erp-nav-item ${isActive(item.path) ? "active" : ""}`}
                  onClick={() => setOpen(false)}
                >
                  <span className="erp-nav-icon">{item.icon}</span>
                  <span>{item.label}</span>
                </Link>
              );
            }
          })}

          <LiveCallIndicator
            activeCall={activeCall}
            onClick={() => navigate(`/live-call/${activeCall.lead.id}`)}
          />
        </nav>

        {/* FOOTER */}
        <div className="erp-footer">
          <Link to="/profile" className="erp-profile">
            <div className="erp-profile-avatar">
              <FaHeadset />
            </div>
            <div>
              <div className="erp-profile-name">{user?.name || "Agent"}</div>
              <div className="erp-profile-role">{user?.role || "User"}</div>
            </div>
          </Link>

          <button className="erp-logout" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
